import { Settings } from "luxon";
import { createContext, useContext, useEffect, useReducer } from "react";

import { settingsAjax } from "../../ajax/SettingsAjax";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { UserDto } from "../../DTOs/User/UserDto";

type TimezoneState = {
  timezone: string | null;
  isLoading: boolean;
  error: string | null;
};

type TimezoneAction =
  | { type: "FETCH_START" }
  | { type: "FETCH_SUCCESS"; payload: string }
  | { type: "FETCH_ERROR"; payload: string };

const initialState: TimezoneState = {
  timezone: null,
  isLoading: false,
  error: null,
};

function timezoneReducer(state: TimezoneState, action: TimezoneAction): TimezoneState {
  switch (action.type) {
    case "FETCH_START":
      return { ...state, isLoading: true, error: null };
    case "FETCH_SUCCESS":
      return { timezone: action.payload, isLoading: false, error: null };
    case "FETCH_ERROR":
      return { ...state, isLoading: false, error: action.payload };
    default:
      return state;
  }
}

const TimezoneContext = createContext<TimezoneContextType>({
  timezone: null,
  isTimezoneLoading: false,
  timezoneFetchError: null
});

type TimezoneContextType = {
  timezone: string | null;
  isTimezoneLoading: boolean;
  timezoneFetchError: string | null
};

export const TimezoneProvider: React.FC<{ children: React.ReactNode, user: UserDto | null }> = ({ children, user }) => {
  const [state, dispatch] = useReducer(timezoneReducer, initialState);

  useEffect(() => {
    if (!user) return;
    dispatch({ type: "FETCH_START" });

    const fetchTimezone = async () => {
      try {
        const response = await settingsAjax.getTimezoneSettings();
        if (response instanceof ErrorResponse) {
          dispatch({ type: "FETCH_ERROR", payload: response.messages[0].description });
          console.error("Failed to fetch timezone");
          return;
        }
        dispatch({ type: "FETCH_SUCCESS", payload: response.timezoneId });
        Settings.defaultZone = response.timezoneId;
      } catch (error) {
        if (error !== "CANCELLED") {
          dispatch({ type: "FETCH_ERROR", payload: "Failed to fetch timezone info" });
          console.log(error)
        }
      }
    };

    fetchTimezone();
  }, [user]);

  return (
    <TimezoneContext.Provider value={{
      timezone: state.timezone,
      isTimezoneLoading: state.isLoading,
      timezoneFetchError: state.error
    }}>
      {children}
    </TimezoneContext.Provider>
  );
};

export const useTimezone = () => useContext(TimezoneContext);